import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  emptyNutrients,
  scaleNutrients,
  sumNutrients,
  type DayTotals,
  type Nutrients,
} from '@nutrigeist/shared';
import { diaryApi, weightApi } from '../api/endpoints.js';
import { useCurrentUser } from '../auth/AuthContext.js';
import { WeightChart } from '../components/WeightChart.js';
import { WeightTrendSummary } from '../components/WeightTrendSummary.js';
import { LoggingStats } from '../components/LoggingStats.js';
import { TdeeCard } from '../components/TdeeCard.js';
import { ChevronLeftIcon } from '../components/icons.js';
import { addDays, formatGrams, formatKcal, formatNumber, todayInTimeZone } from '../lib/format.js';

const RANGES = [
  { days: 7, label: '7 T' },
  { days: 30, label: '30 T' },
  { days: 90, label: '90 T' },
] as const;

/**
 * Weekly and monthly trends on one screen: average intake against goal, how
 * often the calorie goal was met, and the weight trend over the same span.
 *
 * Averages are taken over *logged* days only — days with no entries are almost
 * always days the user forgot to log, not zero-calorie days, and folding them
 * in would understate every average.
 */
export function TrendsPage(): JSX.Element {
  const user = useCurrentUser();
  const timeZone = user.preferences.timezone;
  const goals = user.goals;

  const [rangeDays, setRangeDays] = useState<number>(30);

  const to = todayInTimeZone(timeZone);
  const from = addDays(to, -(rangeDays - 1));

  const rangeQuery = useQuery({
    queryKey: ['diary', 'range', from, to],
    queryFn: () => diaryApi.range({ from, to, timezone: timeZone }),
  });

  const weightQuery = useQuery({
    queryKey: ['weights', 'series', 'trends', rangeDays],
    queryFn: () => weightApi.series({ from: `${from}T00:00:00.000Z`, trendWindow: 7 }),
  });

  const stats = useMemo(
    () => summarise(rangeQuery.data?.days ?? [], goals.kcal),
    [rangeQuery.data, goals.kcal],
  );

  return (
    <div className="stack">
      <div className="page-header">
        <div className="row" style={{ alignItems: 'center', gap: 4 }}>
          <Link to="/" className="icon-btn" aria-label="Zurück zu Heute">
            <ChevronLeftIcon />
          </Link>
          <h1>Verlauf</h1>
        </div>

        <div className="segmented" style={{ gridAutoFlow: 'column' }}>
          {RANGES.map((range) => (
            <button
              key={range.days}
              className={rangeDays === range.days ? 'active' : ''}
              onClick={() => setRangeDays(range.days)}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {rangeQuery.isPending ? (
        <div className="card empty-state">
          <div className="spinner" style={{ margin: '0 auto' }} role="status" aria-label="Lädt" />
        </div>
      ) : stats.loggedDays === 0 ? (
        <div className="card empty-state">
          In diesem Zeitraum wurde nichts erfasst. Sobald du Mahlzeiten einträgst, erscheinen hier
          deine Durchschnitte.
        </div>
      ) : (
        <>
          <div className="card stack">
            <div className="row-between">
              <div>
                <div className="calorie-headline">
                  <span className="value">{formatKcal(stats.avgKcal)}</span>
                  <span className="unit">Ø kcal / Tag</span>
                </div>
                <div className="hint" style={{ marginTop: 4 }}>
                  über {stats.loggedDays}{' '}
                  {stats.loggedDays === 1 ? 'erfassten Tag' : 'erfasste Tage'}
                </div>
              </div>
              {goals.kcal != null && goals.kcal > 0 && (
                <span
                  className={
                    stats.avgKcal <= goals.kcal ? 'badge badge-accent' : 'badge badge-warning'
                  }
                >
                  {formatNumber((stats.avgKcal / goals.kcal) * 100)}% vom Ziel
                </span>
              )}
            </div>

            <IntakeChart days={rangeQuery.data?.days ?? []} goalKcal={goals.kcal} />

            {goals.kcal != null && goals.kcal > 0 && (
              <div className="hint">
                An {stats.daysWithinGoal} von {stats.loggedDays} erfassten Tagen im Kalorienziel (≤{' '}
                {formatKcal(goals.kcal)} kcal).
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-title">Ø Makros pro Tag</div>
            <div className="macro-grid">
              <MacroAverage label="Eiweiß" value={stats.avg.protein} goal={goals.protein} />
              <MacroAverage label="Kohlenh." value={stats.avg.carbs} goal={goals.carbs} />
              <MacroAverage label="Fett" value={stats.avg.fat} goal={goals.fat} />
            </div>
          </div>
        </>
      )}

      <TdeeCard days={rangeDays} />

      <LoggingStats days={rangeDays} />

      <div className="card stack">
        <div className="row-between">
          <div className="card-title">Gewicht</div>
          {weightQuery.data?.changeKg != null && (
            <span className="hint">
              {weightQuery.data.changeKg >= 0 ? '+' : '−'}
              {formatNumber(Math.abs(weightQuery.data.changeKg), 2)} kg (Trend)
            </span>
          )}
        </div>
        {weightQuery.isPending ? (
          <div className="empty-state">
            <div className="spinner" style={{ margin: '0 auto' }} role="status" aria-label="Lädt" />
          </div>
        ) : (
          <>
            <WeightChart days={weightQuery.data?.days ?? []} />
            {weightQuery.data && <WeightTrendSummary series={weightQuery.data} />}
          </>
        )}
      </div>
    </div>
  );
}

function MacroAverage({
  label,
  value,
  goal,
}: {
  label: string;
  value: number;
  goal: number | null;
}): JSX.Element {
  return (
    <div className="macro-cell">
      <div className="macro-value">{formatGrams(value)} g</div>
      <div className="macro-label">{label}</div>
      <div className="hint" style={{ marginTop: 4 }}>
        {goal ? `Ziel ${formatGrams(goal)} g` : ' '}
      </div>
    </div>
  );
}

interface Stats {
  loggedDays: number;
  avgKcal: number;
  avg: Nutrients;
  daysWithinGoal: number;
}

function summarise(days: DayTotals[], goalKcal: number | null): Stats {
  const logged = days.filter((day) => day.entryCount > 0);
  const count = logged.length;

  if (count === 0) {
    return { loggedDays: 0, avgKcal: 0, avg: emptyNutrients(), daysWithinGoal: 0 };
  }

  const avg = scaleNutrients(sumNutrients(logged.map((day) => day.totals)), 1 / count);

  const daysWithinGoal =
    goalKcal != null && goalKcal > 0
      ? logged.filter((day) => day.totals.kcal <= goalKcal).length
      : 0;

  return { loggedDays: count, avgKcal: avg.kcal, avg, daysWithinGoal };
}

const CHART_WIDTH = 320;
const CHART_HEIGHT = 140;
const CHART_PAD = { top: 10, right: 6, bottom: 6, left: 30 };

/**
 * One bar per day of calories, with the goal drawn as a reference line. Days
 * over the goal turn red; days with no entries are a faint stub so gaps in
 * logging stay visible rather than reading as zero-calorie days.
 */
function IntakeChart({
  days,
  goalKcal,
}: {
  days: DayTotals[];
  goalKcal: number | null;
}): JSX.Element {
  const plotWidth = CHART_WIDTH - CHART_PAD.left - CHART_PAD.right;
  const plotHeight = CHART_HEIGHT - CHART_PAD.top - CHART_PAD.bottom;

  const maxKcal = Math.max(goalKcal ?? 0, ...days.map((day) => day.totals.kcal), 1);
  const domainMax = maxKcal * 1.1;

  const y = (value: number): number =>
    CHART_PAD.top + plotHeight - (value / domainMax) * plotHeight;

  const slot = plotWidth / Math.max(days.length, 1);
  const barWidth = Math.max(1, Math.min(slot * 0.7, 14));

  const goalY = goalKcal && goalKcal > 0 ? y(goalKcal) : null;

  return (
    <svg
      className="chart"
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      role="img"
      aria-label="Kalorien pro Tag"
      preserveAspectRatio="none"
      style={{ height: 150 }}
    >
      {[domainMax, domainMax / 2].map((value) => (
        <text
          key={value}
          className="chart-axis-label"
          x={CHART_PAD.left - 4}
          y={y(value) + 3}
          textAnchor="end"
        >
          {formatKcal(value)}
        </text>
      ))}

      {days.map((day, index) => {
        const x = CHART_PAD.left + index * slot + (slot - barWidth) / 2;
        const empty = day.entryCount === 0;
        const over = goalKcal != null && goalKcal > 0 && day.totals.kcal > goalKcal;
        const top = empty ? CHART_PAD.top + plotHeight - 2 : y(day.totals.kcal);
        const height = CHART_PAD.top + plotHeight - top;
        return (
          <rect
            key={day.date}
            x={x}
            y={top}
            width={barWidth}
            height={height}
            rx={barWidth > 4 ? 2 : 0}
            fill={empty ? 'var(--border)' : over ? 'var(--danger)' : 'var(--accent)'}
          >
            <title>{`${day.date}: ${empty ? 'nichts erfasst' : `${formatKcal(day.totals.kcal)} kcal`}`}</title>
          </rect>
        );
      })}

      {goalY != null && (
        <line
          x1={CHART_PAD.left}
          x2={CHART_WIDTH - CHART_PAD.right}
          y1={goalY}
          y2={goalY}
          stroke="var(--text-muted)"
          strokeWidth="1"
          strokeDasharray="4 3"
        />
      )}
    </svg>
  );
}
